import React from 'react';
import { Box, Card, CardContent, Chip, Grid, Paper, Radio, TextField, Typography } from '@mui/material';
import { People } from '@mui/icons-material';

interface CapacityPack {
  capacity: number;
  price: number;
}

interface SignupNucleusCapacitySelectorProps {
  nucleusCapacityPacks: CapacityPack[];
  contractedNucleusCapacity: number;
  fieldError: string | undefined;
  customNucleusCapacityInput: string;
  onCustomInputChange: (raw: string) => void;
  onChangeCapacity: (capacity: number) => void;
  calculateNucleusCapacityCost: (capacity: number) => number;
}

/**
 * Seletor de quantidade de núcleos do plano Institucional no cadastro (pacotes pré-definidos +
 * quantidade personalizada). Extraído de SignupForm.tsx (F7).
 */
export const SignupNucleusCapacitySelector: React.FC<SignupNucleusCapacitySelectorProps> = ({
  nucleusCapacityPacks,
  contractedNucleusCapacity,
  fieldError,
  customNucleusCapacityInput,
  onCustomInputChange,
  onChangeCapacity,
  calculateNucleusCapacityCost,
}) => {
  if (nucleusCapacityPacks.length === 0) return null;

  const isCustom =
    customNucleusCapacityInput !== '' ||
    (contractedNucleusCapacity > 0 && !nucleusCapacityPacks.some((p) => p.capacity === contractedNucleusCapacity));
  const customDisplay =
    customNucleusCapacityInput !== ''
      ? customNucleusCapacityInput
      : isCustom
      ? String(contractedNucleusCapacity || 0)
      : '';

  return (
    <Grid item xs={12}>
      <Card variant="outlined" sx={fieldError ? { borderColor: 'error.main', borderWidth: 2 } : {}}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <People sx={{ mr: 1, color: 'primary.main' }} />
            Quantidade de Núcleos
          </Typography>
          {fieldError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
              {fieldError}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione quantos núcleos (bairros, loteamentos, áreas) sua operação precisará gerenciar:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[...nucleusCapacityPacks]
              .sort((a, b) => a.capacity - b.capacity)
              .map((pack) => {
                const isSelected = customNucleusCapacityInput === '' && contractedNucleusCapacity === pack.capacity;
                return (
                  <Paper
                    key={pack.capacity}
                    onClick={() => {
                      onCustomInputChange('');
                      onChangeCapacity(pack.capacity);
                    }}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'primary.main' : 'background.paper',
                      color: isSelected ? 'white' : 'inherit',
                      '&:hover': { bgcolor: isSelected ? 'primary.dark' : 'action.hover', borderColor: 'primary.main' },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Radio checked={isSelected} sx={isSelected ? { color: 'white', '&.Mui-checked': { color: 'white' } } : {}} />
                      <Typography sx={{ color: 'inherit' }}>{pack.capacity} núcleos - R$ {pack.price.toFixed(2)}/mês</Typography>
                      <Chip label={`R$ ${(pack.price / pack.capacity).toFixed(2)}/núcleo`} size="small" color="success" variant="outlined" />
                    </Box>
                  </Paper>
                );
              })}
            <Paper
              onClick={() => {
                onCustomInputChange(customDisplay || '');
                document.getElementById('signup-custom-nucleus-input')?.focus();
              }}
              sx={{
                p: 2,
                cursor: 'pointer',
                border: 2,
                borderColor: isCustom ? 'primary.main' : 'divider',
                bgcolor: isCustom ? 'primary.main' : 'background.paper',
                color: isCustom ? 'white' : 'inherit',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Radio checked={isCustom} sx={isCustom ? { color: 'white', '&.Mui-checked': { color: 'white' } } : {}} />
                <Typography sx={{ color: 'inherit' }}>Outra quantidade:</Typography>
                <TextField
                  id="signup-custom-nucleus-input"
                  type="number"
                  size="small"
                  value={customDisplay}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const raw = e.target.value;
                    onCustomInputChange(raw);
                    const value = raw === '' ? 0 : parseInt(raw, 10);
                    onChangeCapacity(!isNaN(value) && value >= 1 ? value : 0);
                  }}
                  onFocus={(e) => e.stopPropagation()}
                  placeholder="Ex: 4, 8"
                  inputProps={{ min: 1, style: { width: '80px', textAlign: 'center' } }}
                  sx={{ width: '120px' }}
                />
                <Typography variant="body2" color="text.secondary">núcleos</Typography>
                {isCustom && contractedNucleusCapacity >= 1 && (
                  <Chip label={`R$ ${calculateNucleusCapacityCost(contractedNucleusCapacity).toFixed(2)}/mês`} size="small" color="info" variant="outlined" />
                )}
              </Box>
            </Paper>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default SignupNucleusCapacitySelector;
